import {
  ArrowRight,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Server,
  RefreshCw,
  ShieldCheck,
  Activity,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Loading } from '../components/Loading';
import { StatusBadge } from '../components/StatusBadge';
import { api, dateTime } from '../lib/api';
import { Link, useNavigate } from '../lib/router';
import type { Customer } from '../lib/types';

type Dashboard = {
  metrics: {
    totalCustomers: number;
    pendingKyc: number;
    approvedToday: number;
    partnerFailures: number;
  };
  queue: Customer[];
  integration: {
    quiApi: string;
    partnerApi: string;
    updatedAt: string;
  };
};

export function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setRefreshing(true);
    setError('');
    api<Dashboard>('/dashboard')
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError('Não foi possível atualizar a dashboard. Tenta novamente.'); })
      .finally(() => { if (active) setRefreshing(false); });
    return () => { active = false; };
  }, [retry]);

  if (!data && !error) return <Loading label="A preparar a dashboard" />;
  if (!data) return <div className="empty-state" role="alert"><CircleAlert size={32} /><h3>Não foi possível carregar a dashboard</h3><p>{error}</p><button className="button button--secondary" onClick={() => setRetry(value => value + 1)}>Tentar novamente</button></div>;

  const cards = [
    {
      label: 'Total de clientes',
      value: data.metrics.totalCustomers.toLocaleString('pt-PT'),
      note: 'Contas registadas',
      icon: Users,
      tone: 'neutral',
    },
    {
      label: 'KYC por rever',
      value: data.metrics.pendingKyc,
      note: data.metrics.pendingKyc ? 'Processos a aguardar revisão' : 'Sem processos pendentes',
      icon: Clock3,
      tone: 'warning',
    },
    {
      label: 'Aprovados hoje',
      value: data.metrics.approvedToday,
      note: 'Decisões concluídas',
      icon: CheckCircle2,
      tone: 'success',
    },
    {
      label: 'Falhas Eupago',
      value: data.metrics.partnerFailures,
      note: 'Integrações com erro',
      icon: CircleAlert,
      tone: 'danger',
    },
  ];

  return (
    <div className="page operations-dashboard">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Centro de operações</span>
          <h1>Visão geral</h1>
          <p>Acompanhe as validações e integrações que precisam da sua equipa.</p>
        </div>
        <button className="button button--secondary" disabled={refreshing} onClick={() => setRetry(value => value + 1)}><RefreshCw size={16} className={refreshing ? 'dashboard-refreshing' : ''} />{refreshing ? 'A atualizar…' : 'Atualizar dados'}</button>
      </div>

      {error && <div className="form-error" role="alert">{error} Os dados anteriores continuam visíveis.</div>}
      <section className="dashboard-focus">
        <div className="dashboard-focus__copy"><span className="dashboard-kicker"><ShieldCheck size={16} /> Revisão de clientes</span><h2>{data.metrics.pendingKyc ? 'Cada revisão faz a diferença.' : 'Tudo a postos para a próxima revisão.'}</h2><p>{data.metrics.pendingKyc ? `${data.metrics.pendingKyc.toLocaleString('pt-PT')} processos KYC aguardam a atenção da equipa. Consulta os dados e documentos antes de decidir.` : 'Consulta os processos e acompanha as validações dos teus clientes.'}</p><Link className="button button--primary" to="/customers?queue=kyc">Abrir fila de revisão <ArrowRight size={16} /></Link></div>
        <div className="dashboard-focus__stat"><span>Por rever</span><strong>{data.metrics.pendingKyc.toLocaleString('pt-PT')}</strong><small>processos KYC</small></div>
      </section>
      <section className="dashboard-metrics" aria-label="Indicadores de operação">
        {cards.map(({ label, value, note, icon: Icon, tone }) => (
          <article className={`dashboard-metric dashboard-metric--${tone}`} key={label}>
            <div className="dashboard-metric__top"><span>{label}</span><span className="dashboard-metric__icon"><Icon size={19} /></span></div>
            <strong>{value}</strong>
            <small>{note}</small>
          </article>
        ))}
      </section>

      <div className="dashboard-grid">
        <section className="panel panel--wide">
          <div className="panel__heading">
            <div>
              <h2>Fila de revisão</h2>
              <p>Pedidos mais recentes que aguardam decisão.</p>
            </div>
            <Link className="dashboard-text-link" to="/customers?queue=kyc">Ver fila completa <ArrowRight size={14} /></Link>
          </div>
          {!data.queue.length ? <div className="dashboard-empty"><CheckCircle2 size={30} /><h3>Sem pedidos nesta lista</h3><p>Os pedidos disponibilizados para revisão aparecem aqui.</p><Link to="/customers?queue=kyc" className="dashboard-text-link">Consultar fila completa <ArrowRight size={14} /></Link></div> : <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Registo</th>
                  <th>Risco</th>
                  <th>Estado</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {data.queue.map((customer) => (
                  <tr
                    key={customer.id}
                    className="table-row-link"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <td>
                      <div className="customer-cell">
                        <span className="avatar avatar--small">
                          {customer.fullName.slice(0, 2).toUpperCase()}
                        </span>
                        <div>
                          <strong>{customer.fullName}</strong>
                          <small>{customer.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>{dateTime(customer.createdAt)}</td>
                    <td><StatusBadge value={customer.riskLevel} /></td>
                    <td><StatusBadge value={customer.kycStatus} /></td>
                    <td>
                      <Link className="table-action" to={`/customers/${customer.id}`}>
                        Rever <ArrowRight size={15} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
        </section>

        <aside className="dashboard-side"><section className="panel integration-panel">
          <div className="panel__heading">
            <div>
              <h2>Estado dos serviços</h2>
              <p>Última verificação {dateTime(data.integration.updatedAt)}</p>
            </div>
            <Server size={20} />
          </div>
          <ServiceRow label="QUI API" status={data.integration.quiApi} />
          <ServiceRow label="Partner API" status={data.integration.partnerApi} />
          <div className="integration-panel__note">
            <Activity size={15} />
            Estado reportado na última verificação
          </div>
          </section>
          <section className="panel dashboard-shortcuts"><div className="panel__heading"><div><h2>Acessos rápidos</h2><p>Continua a gestão do backoffice.</p></div></div><Link to="/customers"><span className="dashboard-shortcut-icon"><Users size={19} /></span><span><strong>Clientes</strong><small>Consultar contas e dados</small></span><ArrowRight size={16} /></Link><Link to="/customers?queue=kyc"><span className="dashboard-shortcut-icon"><ShieldCheck size={19} /></span><span><strong>Revisão KYC</strong><small>Analisar processos pendentes</small></span><ArrowRight size={16} /></Link></section>
        </aside>
      </div>
    </div>
  );
}

function ServiceRow({ label, status }: { label: string; status: string }) {
  return (
    <div className="service-row">
      <span className="dashboard-service-name"><Server size={16} />{label}</span>
      <strong className={`service-state service-state--${status.toLowerCase()}`}>
        <span className="status__dot" />{{ online: 'Disponível', offline: 'Indisponível', degraded: 'Degradado', unavailable: 'Indisponível', unknown: 'Desconhecido' }[status.toLowerCase()] ?? status}
      </strong>
    </div>
  );
}
